// src/pages/Profile.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';
import BiometricManager from './BiometricManager';


function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function Profile() {
  const { staff, logout, login } = useAuth();
  const [showChangePin, setShowChangePin] = useState(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('changePin') === '1'
  );
  const [showEditInfo, setShowEditInfo] = useState(false);
  // Set when an admin reset the PIN, or when a weak PIN was found during a
  // security review. The staff member cannot be left guessing why.
  const mustChangePin = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('changePin') === '1';
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [editForm, setEditForm] = useState({
    email: staff?.email || '',
    phone: staff?.phone || '',
    alertPhone: staff?.alertPhone || '',
    receivesAlerts: staff?.receivesAlerts || false,
  });

  const handleChangePin = async (e) => {
    e.preventDefault();
    if (newPin !== confirmPin) { setError('New PINs do not match'); return; }
    if (!/^\d{6}$/.test(newPin)) { setError('PIN must be exactly 6 digits'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.post('/auth/change-pin', { currentPin, newPin });
      setSuccess('PIN changed successfully.');
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
      setShowChangePin(false);
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.patch(`/staff/${staff.id}/self`, editForm);
      setSuccess('Information updated.');
      setShowEditInfo(false);
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const initials = `${staff?.firstName?.[0] || ''}${staff?.lastName?.[0] || ''}`;

  return (
    <PageWrapper active="profile" title="My Profile">
      <div style={{ maxWidth: 500 }}>

        {/* Sign out - always visible at top */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button onClick={logout} style={{ fontSize: 13, color: 'var(--gray-600)', background: 'none', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
            Sign out
          </button>
        </div>

        {/* Staff info card */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
              {initials}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 18 }}>{staff?.firstName} {staff?.lastName}</div>
              <div style={{ fontSize: 13, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{staff?.role}</div>
            </div>
          </div>

          {!showEditInfo ? (
            <div>
              {staff?.email && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>📧 {staff.email}</div>}
              {staff?.phone && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>📱 {staff.phone}</div>}
              {staff?.receivesAlerts && <div style={{ fontSize: 12, color: 'var(--primary)', marginBottom: 8 }}>✓ Receiving SMS alerts</div>}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => { setShowEditInfo(true); setError(''); setSuccess(''); }}>Edit my info</button>
            </div>
          ) : (
            <form onSubmit={handleSaveInfo}>
              <div className="field"><label>Email</label><input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div className="field"><label>Phone</label><input type="tel" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} /></div>
              <div className="field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={editForm.receivesAlerts} onChange={e => setEditForm(f => ({ ...f, receivesAlerts: e.target.checked }))} style={{ width: 'auto' }} />
                  Receive overdue SMS alerts
                </label>
              </div>
              {editForm.receivesAlerts && (
                <div className="field"><label>Alert phone number</label><input type="tel" value={editForm.alertPhone} onChange={e => setEditForm(f => ({ ...f, alertPhone: e.target.value }))} placeholder="+1 (310) 555-0000" /></div>
              )}
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              {success && <div className="alert-bar success" style={{ marginBottom: 8 }}>{success}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowEditInfo(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>

        {/* Security */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 14, color: 'var(--primary)' }}>Security</div>
          {mustChangePin && !success && (
            <div className="alert-bar warning" style={{ marginBottom: 12, background: 'var(--amber-light)', color: 'var(--amber)' }}>
              <strong>Your PIN needs to be changed.</strong> A security review found it was one that could be guessed.
              Please choose a new one now — avoid repeated digits, runs like 123456, and dates.
            </div>
          )}
          {!showChangePin ? (
            <button className="btn btn-secondary" onClick={() => { setShowChangePin(true); setError(''); setSuccess(''); }}>Change my PIN</button>
          ) : (
            <form onSubmit={handleChangePin}>
              <div className="field"><label>Current PIN</label>
              <div style={{ position: 'relative' }}>
                <input type={showCurrentPin ? 'text' : 'password'} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={currentPin} onChange={e => setCurrentPin(e.target.value.replace(/\D/g, ''))} required style={{ paddingRight: 40 }} />
                <button type="button" onClick={() => setShowCurrentPin(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                  {showCurrentPin ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="field"><label>New PIN (exactly 6 digits)</label>
                <div style={{ position: 'relative' }}>
                  <input type={showNewPin ? 'text' : 'password'} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} required style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowNewPin(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                    {showNewPin ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>
                <div className="field"><label>Confirm new PIN</label>
                <div style={{ position: 'relative' }}>
                  <input type={showNewPin ? 'text' : 'password'} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))} required style={{ paddingRight: 40 }} />
                </div>
              </div>
              </div>
              {error && <div className="alert-bar danger" style={{ marginBottom: 8 }}>{error}</div>}
              {success && <div className="alert-bar success" style={{ marginBottom: 8 }}>{success}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update PIN'}</button>
                <button className="btn btn-secondary" type="button" onClick={() => setShowChangePin(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>

        {/* Biometric login */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 14, color: 'var(--primary)' }}>Face ID / Touch ID</div>
          <BiometricManager staffId={staff?.id} />
        </div>

        {/* HIPAA notice */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 10, color: 'var(--primary)' }}>HIPAA Notice</div>
          <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.7 }}>
            All activity in this system is logged per HIPAA requirements. Your name is attached to every check you submit. Keep your PIN private and never share your login credentials.
          </p>
        </div>

      </div>
    </PageWrapper>
  );
}
